import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  superCreateUser,
  superDeleteUser,
  type UserRow,
  type SucursalRow,
} from "../../lib/api";
import { Modal } from "../../components/ui/Modal";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";
import { useAdminCtx } from "./AdminLayout";
import { useDialogs } from "../../hooks/useDialogs";

// Etiquetas legibles del rol. La DB guarda el string crudo
// (super_admin / admin); en la UI mostramos castellano para no filtrar
// jerga interna a quien administra usuarios.
const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super admin",
  admin: "Admin",
};

// Cuerpo de la pestaña "Usuarios" dentro de Organizacion. NO trae su
// propio <h1> ni hace fetch: el padre (Organizacion) carga usuarios +
// sucursales en un solo Promise.all y nos pasa ambas listas + un refetch.
export function UsuariosPanel({
  users,
  sucursales,
  refetch,
}: {
  users: UserRow[];
  sucursales: SucursalRow[];
  refetch: () => void;
}) {
  const { user } = useAdminCtx();
  const dialogs = useDialogs();
  const [creating, setCreating] = useState(false);

  async function handleDelete(u: UserRow) {
    if (u.email === user.email) {
      await dialogs.alert({
        title: "Acción bloqueada",
        message: "No te podés borrar a vos mismo.",
        variant: "error",
      });
      return;
    }
    const ok = await dialogs.confirm({
      title: "Borrar acceso de usuario",
      message: `¿Borrar acceso de ${u.email}? Esa persona deja de poder ingresar al admin.`,
      confirmLabel: "Borrar",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await superDeleteUser(u.email);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end gap-2">
        <HelpPopover label="Después del alta" align="right">
          Recordá: tras dar de alta acá, también hay que agregar el email a la
          lista de Cloudflare Access permitidos (Cloudflare dashboard).
        </HelpPopover>
        <button
          onClick={() => setCreating(true)}
          data-tour="usuarios-new"
          className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-4 py-2 font-bold"
        >
          <Plus className="h-4 w-4" />
          Alta de usuario
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white/5">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-left opacity-70">
            <tr>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2 w-32">Rol</th>
              <th className="px-4 py-2">Sucursal</th>
              <th className="px-4 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.email} className="border-b border-white/5">
                <td className="px-4 py-2 font-medium">{u.email}</td>
                <td className="px-4 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${
                      u.role === "super_admin"
                        ? "bg-purple-500/20 text-purple-300"
                        : "bg-blue-500/20 text-blue-300"
                    }`}
                  >
                    {ROLE_LABELS[u.role] ?? u.role}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs opacity-70">
                  {u.sucursal_id
                    ? sucursales.find((s) => s.id === u.sucursal_id)?.nombre ??
                      `#${u.sucursal_id}`
                    : "—"}
                </td>
                <td className="px-4 py-2">
                  {u.email !== user.email && (
                    <button
                      onClick={() => handleDelete(u)}
                      className="rounded p-1.5 text-red-400 hover:bg-red-500/15"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} closeOnBackdrop>
        <UserForm
          sucursales={sucursales}
          onCancel={() => setCreating(false)}
          onSubmit={async (data) => {
            await superCreateUser(data);
            setCreating(false);
            refetch();
          }}
        />
      </Modal>
    </div>
  );
}

function UserForm({
  sucursales,
  onSubmit,
  onCancel,
}: {
  sucursales: SucursalRow[];
  onSubmit: (data: { email: string; role: "super_admin" | "admin"; sucursal_id?: number }) => Promise<void>;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"super_admin" | "admin">("admin");
  const [sucursalId, setSucursalId] = useState<number>(
    sucursales[0]?.id ?? 1,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit({
        email: email.trim().toLowerCase(),
        role,
        sucursal_id: role === "admin" ? sucursalId : undefined,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">Alta de usuario</h2>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="marketingcde@gmail.com"
          required
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Rol</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as "super_admin" | "admin")}
          className="rounded-lg bg-white/10 px-3 py-2 outline-none"
        >
          <option value="admin">Admin</option>
          <option value="super_admin">Super admin</option>
        </select>
      </label>

      {role === "admin" && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="opacity-80">Sucursal</span>
          <select
            value={sucursalId}
            onChange={(e) => setSucursalId(parseInt(e.target.value))}
            className="rounded-lg bg-white/10 px-3 py-2 outline-none"
            required
          >
            {sucursales.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && (
        <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold disabled:opacity-50"
        >
          {loading ? "Creando…" : "Dar de alta"}
        </button>
      </div>
    </form>
  );
}
